import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { IdChecksDpiaStatus, IdChecksGroupSettings as IdChecksGroupSettingsSchema } from '@cloudsign/shared';
import type { IdChecksGroupSettings, IdDocumentType } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { formatDateTime } from '../../lib/format';
import { useCan } from '../../lib/session';
import { SettingsEditor } from '../../components/SettingsEditor';
import type { SettingsCardProps } from '../../components/SettingsCards';
import { ErrorLine, ErrorPanel } from '../../components/ErrorPanel';
import { Alert, Badge, Button, Card, CheckboxField, ConfirmButton, PageHeader, SelectField, Spinner, TextField } from '../../components/ui';
import { useScopedSettings } from '../sites/api';
import { isConflict } from './api';

const DPIA_KEY = ['id-checks', 'dpia'] as const;
const DOC_TYPES: IdDocumentType[] = ['passport', 'driving_licence', 'national_id'];

function useDpia(enabled = true) {
  return useQuery({ queryKey: DPIA_KEY, enabled, queryFn: () => api.get('/id-checks/dpia', { schema: IdChecksDpiaStatus }) });
}

function DpiaCard() {
  const qc = useQueryClient();
  const q = useDpia();
  const [reference, setReference] = useState('');
  const [ticked, setTicked] = useState(false);
  const confirm = useMutation({
    mutationFn: () => api.post('/id-checks/dpia', { body: { confirm: true, reference }, schema: IdChecksDpiaStatus }),
    onSuccess: (s) => {
      qc.setQueryData(DPIA_KEY, s);
      setReference('');
      setTicked(false);
    },
  });
  const withdraw = useMutation({
    mutationFn: () => api.delete('/id-checks/dpia'),
    onSuccess: () => qc.invalidateQueries({ queryKey: DPIA_KEY }),
  });
  if (q.isError) return <ErrorPanel error={q.error} onRetry={() => void q.refetch()} />;
  if (!q.data) return <Spinner />;
  const s = q.data;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    confirm.mutate();
  };
  return (
    <Card title={t('setup.idc.dpia.title')} description={t('setup.idc.dpia.description')} className="mb-6">
      {s.confirmed ? (
        <div className="space-y-3">
          <p>
            <Badge tone="good">{t('setup.idc.dpia.confirmed')}</Badge>
          </p>
          <dl className="grid gap-3 text-sm sm:grid-cols-3">
            <div>
              <dt className="text-xs font-medium text-slate-600">{t('setup.idc.dpia.reference')}</dt>
              <dd className="break-words">{s.reference ?? ''}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-600">{t('setup.idc.dpia.by')}</dt>
              <dd>{s.confirmedByLabel ?? ''}</dd>
            </div>
            <div>
              <dt className="text-xs font-medium text-slate-600">{t('setup.idc.dpia.on')}</dt>
              <dd>{formatDateTime(s.confirmedAt)}</dd>
            </div>
          </dl>
          <Alert kind="info">{t('setup.idc.dpia.withdrawWarning')}</Alert>
          <ErrorLine error={withdraw.error} />
          <ConfirmButton label={t('setup.idc.dpia.withdraw')} confirmLabel={t('setup.idc.dpia.withdrawConfirm')} pending={withdraw.isPending} onConfirm={() => withdraw.mutate()} />
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <Badge tone="warn">{t('setup.idc.dpia.notConfirmed')}</Badge>
          <p className="text-sm text-slate-700">{t('setup.idc.dpia.explain')}</p>
          <ErrorLine error={confirm.error} />
          <TextField label={t('setup.idc.dpia.reference')} hint={t('setup.idc.dpia.referenceHint')} required maxLength={200} value={reference} onChange={(e) => setReference(e.target.value)} />
          <CheckboxField label={t('setup.idc.dpia.tick')} checked={ticked} onChange={(e) => setTicked(e.target.checked)} />
          <Button type="submit" variant="primary" disabled={confirm.isPending || !ticked || reference.trim() === ''}>
            {t('setup.idc.dpia.confirm')}
          </Button>
        </form>
      )}
    </Card>
  );
}

/** Tenant page "ID checks": the DPIA gate (confirm, withdraw) and the result retention rule. Needs id_checks.manage. */
export function IdChecksPage() {
  return (
    <>
      <PageHeader title={t('setup.idc.title')} description={t('setup.idc.description')} />
      <DpiaCard />
      <Card title={t('setup.idc.retention.title')} description={t('setup.idc.retention.description')}>
        <SettingsEditor moduleKey="id-checks" base="/settings" canEdit />
      </Card>
      <p className="mt-6 text-sm text-slate-600">{t('setup.idc.groupsHint')}</p>
    </>
  );
}

type Draft = Partial<IdChecksGroupSettings>;

/**
 * Group card "ID check" (id-checks.md section 2). Off by default. Turning a group on, or storing images, needs the DPIA:
 * the controls stay locked until it is confirmed, and a 409 from the server (the real check) is explained in place.
 * Reads through the group settings route and saves through PUT /id-checks/groups/:groupId/settings (the DPIA gate is there).
 */
export function IdChecksGroupCard({ scopeId, canEdit }: SettingsCardProps) {
  const canManage = useCan('id_checks.manage');
  const dpia = useDpia(canManage);
  const qc = useQueryClient();
  const settings = useScopedSettings(`/groups/${scopeId}/settings`, 'id-checks');
  const save = useMutation({
    mutationFn: (patch: Draft) => api.put(`/id-checks/groups/${scopeId}/settings`, { body: patch, schema: IdChecksGroupSettingsSchema }),
    onSuccess: (values) => {
      qc.setQueryData(['settings', `/groups/${scopeId}/settings`, 'id-checks'], (old: { values: Record<string, unknown> } | undefined) => (old ? { ...old, values } : old));
    },
  });
  const [draft, setDraft] = useState<Draft>({});
  const saved = settings.values as Partial<IdChecksGroupSettings> | undefined;
  useEffect(() => setDraft({}), [saved]);
  if (settings.query.isError) return <ErrorPanel error={settings.query.error} onRetry={() => void settings.query.refetch()} />;
  if (!saved) return <Spinner />;
  const v = { mode: 'off', documentTypes: DOC_TYPES, onFail: 'warn', storeImage: false, imageRetentionDays: 30, repeatEveryDays: 0, ...saved, ...draft } as IdChecksGroupSettings;
  const set = (p: Draft) => setDraft((d) => ({ ...d, ...p }));
  const changed = Object.keys(draft).length > 0;
  // Unknown (no id_checks.manage) is treated as not confirmed in the UI; the server decides.
  const gated = dpia.data?.confirmed !== true;
  const off = v.mode === 'off';
  const toggleDoc = (d: IdDocumentType) => {
    const next = v.documentTypes.includes(d) ? v.documentTypes.filter((x) => x !== d) : [...v.documentTypes, d];
    if (next.length > 0) set({ documentTypes: next });
  };
  return (
    <Card
      title={t('setup.idc.group.title')}
      description={t('setup.idc.group.description')}
      footer={
        canEdit ? (
          <>
            <Button disabled={!changed} onClick={() => setDraft({})}>
              {t('settings.discard')}
            </Button>
            <Button variant="primary" disabled={!changed || save.isPending} onClick={() => save.mutate(draft, { onSuccess: () => setDraft({}) })}>
              {t('common.save')}
            </Button>
          </>
        ) : undefined
      }
    >
      <div className="space-y-4">
        {isConflict(save.error) ? (
          <Alert>
            {t('setup.idc.group.dpiaRequired')}{' '}
            <Link className="underline" to="/manage/id-checks">
              {t('setup.idc.group.dpiaLink')}
            </Link>
          </Alert>
        ) : (
          <ErrorLine error={save.error} />
        )}
        {gated && off ? (
          <Alert kind="info">
            {t('setup.idc.group.locked')}{' '}
            {canManage ? (
              <Link className="underline" to="/manage/id-checks">
                {t('setup.idc.group.dpiaLink')}
              </Link>
            ) : null}
          </Alert>
        ) : null}
        <SelectField label={t('setup.idc.group.mode')} value={v.mode} disabled={!canEdit || (gated && off)} onChange={(e) => set({ mode: e.target.value as IdChecksGroupSettings['mode'] })}>
          {(['off', 'optional', 'required'] as const).map((m) => (
            <option key={m} value={m}>
              {t(`setup.idc.group.mode.${m}`)}
            </option>
          ))}
        </SelectField>
        <fieldset className="space-y-2" disabled={!canEdit || off}>
          <legend className="mb-1 text-sm font-medium text-slate-800">{t('setup.idc.group.documents')}</legend>
          {DOC_TYPES.map((d) => (
            <CheckboxField key={d} label={t(`setup.idc.doc.${d}`)} checked={v.documentTypes.includes(d)} onChange={() => toggleDoc(d)} />
          ))}
        </fieldset>
        <SelectField label={t('setup.idc.group.onFail')} hint={t('setup.idc.group.onFailHint')} value={v.onFail} disabled={!canEdit || off} onChange={(e) => set({ onFail: e.target.value as IdChecksGroupSettings['onFail'] })}>
          <option value="warn">{t('setup.idc.group.onFail.warn')}</option>
          <option value="refuse">{t('setup.idc.group.onFail.refuse')}</option>
        </SelectField>
        <TextField
          type="number"
          min={0}
          max={3650}
          label={t('setup.idc.group.repeat')}
          hint={t('setup.idc.group.repeatHint')}
          value={v.repeatEveryDays}
          disabled={!canEdit || off}
          onChange={(e) => set({ repeatEveryDays: Math.max(0, Math.min(3650, Number(e.target.value) || 0)) })}
        />
        <CheckboxField label={t('setup.idc.group.storeImage')} hint={t('setup.idc.group.storeImageHint')} checked={v.storeImage} disabled={!canEdit || off || gated} onChange={(e) => set({ storeImage: e.target.checked })} />
        {v.storeImage ? (
          <TextField
            type="number"
            min={1}
            max={90}
            label={t('setup.idc.group.imageDays')}
            hint={t('setup.idc.group.imageDaysHint')}
            value={v.imageRetentionDays}
            disabled={!canEdit}
            onChange={(e) => set({ imageRetentionDays: Math.max(1, Math.min(90, Number(e.target.value) || 1)) })}
          />
        ) : null}
      </div>
    </Card>
  );
}
